import { useWorkspaceText } from '../workspace/interface-copy';
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDownIcon, CrownIcon, UserMinusIcon } from 'lucide-react';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  MoreButton,
  RowAction,
} from '@/components/ui/menu';
import { useRequestedAppLocale } from '@/i18n/context';
import { formatAppDate } from '@/i18n/format';
import { BottomSheet, SheetRadioList } from '@/components/ui/sheet';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useNarrowViewport } from '@/lib/mobile';
import { overlaysCopy } from '@/i18n/ns/overlays';
import teamCopy from '@/i18n/ns/team';
import companySettingsCopy from '@/i18n/ns/company-settings';
import type { TeamData, TeamPerson } from './team-api';

/**
 * One choice of a few as a kit menu (a bottom sheet on a phone): the role and publishing pickers.
 * The values are the API's own; `label` names the control for assistive technology.
 */
export function ChoiceMenu({
  label,
  title,
  value,
  options,
  disabled,
  onChange,
}: {
  /** Names the control for assistive technology ("Role for sara@example.com"). */
  label: string;
  /** The phone sheet's title ("Role", "Publishing permission"). */
  title: string;
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const current = options.find((option) => option.value === value) ?? options[0];
  const narrow = useNarrowViewport();
  const [open, setOpen] = useState(false);
  // Crossing the phone breakpoint never carries an open sheet across: closed on every change.
  useEffect(() => setOpen(false), [narrow]);
  const trigger = useRef<HTMLButtonElement>(null);
  const button = (
    <button
      ref={trigger}
      type="button"
      className="company-team-choice"
      aria-label={label}
      aria-haspopup={narrow ? 'dialog' : undefined}
      disabled={disabled}
      onClick={narrow ? () => setOpen(true) : undefined}
    >
      <span>{current.label}</span>
      <ChevronDownIcon aria-hidden="true" />
    </button>
  );
  // A phone gets the kit bottom sheet (titled, scrim, handle); a pointer screen keeps the shared
  // menu. Same values, same one-choice behaviour; a pick reports only when it changes the value.
  if (narrow)
    return (
      <>
        {button}
        <BottomSheet open={open} onOpenChange={setOpen} title={title} returnFocus={trigger}>
          <SheetRadioList
            label={label}
            choices={options}
            value={value}
            onChoose={(next) => {
              setOpen(false);
              if (next !== value) onChange(next);
            }}
          />
        </BottomSheet>
      </>
    );
  return (
    <Menu>
      <MenuTrigger asChild disabled={disabled}>
        {button}
      </MenuTrigger>
      <MenuContent label={label} align="start">
        <MenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <MenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

export function TeamMember({
  person,
  owner,
  busy,
  onSave,
  onRemove,
  onTransfer,
}: {
  person: TeamPerson;
  owner: boolean;
  busy: boolean;
  onSave: (person: TeamPerson) => void;
  onRemove: () => void;
  onTransfer: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(overlaysCopy);
  const companyCopy = useNamespaceCopy(companySettingsCopy);
  const [draft, setDraft] = useState(person);
  useEffect(() => setDraft(person), [person]);
  const dirty = draft.role !== person.role || draft.canPublish !== person.canPublish;
  const roleLabel =
    person.role === 'owner'
      ? companyCopy.owner
      : person.role === 'admin'
        ? companyCopy.admin
        : companyCopy.member;
  return (
    <article className="company-team-member">
      <div className="company-team-identity">
        <span className="company-team-avatar" aria-hidden="true">
          {(person.displayName || person.email).slice(0, 1).toUpperCase()}
        </span>
        <div>
          <strong>{person.displayName || person.email}</strong>
          {person.displayName && <span>{person.email}</span>}
        </div>
      </div>
      <div className="company-team-role">
        {owner && person.role !== 'owner' ? (
          <ChoiceMenu
            label={uiText('{value1} for {value2}', {
              value1: companyCopy.role,
              value2: person.email,
            })}
            title={companyCopy.role}
            value={draft.role}
            disabled={busy}
            options={[
              { value: 'admin', label: companyCopy.admin },
              { value: 'member', label: companyCopy.member },
            ]}
            onChange={(value) => setDraft({ ...draft, role: value as TeamPerson['role'] })}
          />
        ) : (
          <span className="company-team-role-label">{roleLabel}</span>
        )}
      </div>
      <div className="company-team-publishing">
        {owner ? (
          <ChoiceMenu
            label={uiText('{value1} for {value2}', {
              value1: companyCopy.publishingPermission,
              value2: person.email,
            })}
            title={companyCopy.publishingPermission}
            value={draft.canPublish === null ? 'default' : String(draft.canPublish)}
            disabled={busy}
            options={[
              {
                value: 'default',
                label:
                  draft.role === 'member'
                    ? companyCopy.needsApprovalDefault
                    : companyCopy.canPublishDefault,
              },
              { value: 'true', label: companyCopy.canPublish },
              { value: 'false', label: companyCopy.needsApproval },
            ]}
            onChange={(value) =>
              setDraft({
                ...draft,
                canPublish: value === 'default' ? null : value === 'true',
              })
            }
          />
        ) : (
          <span>
            {(person.canPublish ?? person.role !== 'member')
              ? companyCopy.canPublish
              : companyCopy.needsApproval}
          </span>
        )}
      </div>
      <div className="company-team-member-actions">
        {owner && dirty && (
          <button
            className="company-team-save"
            disabled={busy}
            aria-label={companyCopy.savePermissions.replace('{email}', person.email)}
            onClick={() => onSave(draft)}
          >
            {companyCopy.save}
          </button>
        )}
        {owner && person.role !== 'owner' && (
          <Menu>
            <MenuTrigger asChild disabled={busy}>
              <MoreButton label={formatCopy(copy.moreActionsFor, { name: person.email })} />
            </MenuTrigger>
            <MenuContent label={formatCopy(copy.moreActionsFor, { name: person.email })}>
              <MenuItem icon={<CrownIcon aria-hidden="true" />} onSelect={onTransfer}>
                {companyCopy.transferOwnership}
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                icon={<UserMinusIcon aria-hidden="true" />}
                tone="danger"
                onSelect={onRemove}
              >
                {companyCopy.removeAccess}
              </MenuItem>
            </MenuContent>
          </Menu>
        )}
      </div>
    </article>
  );
}

type Invitation = TeamData['invitations'][number];

/**
 * A pending invitation. Revoking is its only action: a quiet danger button that
 * shows on hover or focus (always on touch), not a ⋯ that holds one item (§6.6).
 * It asks first; the confirm dialog and the toast live in the Team page.
 */
export function TeamInvitation({
  invitation,
  busy,
  onRevoke,
}: {
  invitation: Invitation;
  busy: boolean;
  onRevoke: () => void;
}) {
  const copy = useNamespaceCopy(teamCopy);
  const companyCopy = useNamespaceCopy(companySettingsCopy);
  const locale = useRequestedAppLocale();
  const email = useId();
  return (
    <article className="company-team-invitation pc-reveal-host">
      <div>
        <strong id={email}>{invitation.email}</strong>
        <span>
          {invitation.role === 'admin' ? companyCopy.admin : companyCopy.member} ·{' '}
          {companyCopy.expires} {formatAppDate(invitation.expiresAt, locale)}
        </span>
      </div>
      <RowAction
        describedBy={email}
        item={{
          key: 'revoke',
          label: copy.revoke,
          tone: 'danger',
          disabled: busy,
          onSelect: onRevoke,
        }}
      />
    </article>
  );
}
